import React, { useRef, useEffect, useState, ChangeEvent, DragEvent } from "react";
import "./ViewerMain.css";
import Loading from "./Loading";
import { Tooltip } from 'react-tooltip';
import { useSelector, useDispatch } from 'react-redux';
import { handleFiles, checkIsLink, deepClone, animation } from "../../../../utils/helpers";
import { updateHoverIndex } from "../../../../store/features/hoverIndexSlice";
import { DivRef, InputRef, PreArrRef, CanvasArrRef } from "../../../../utils/typesAndMaps";
import { DemoImageData } from "../../../../store/features/imagesDataSlice";
import { RootStore } from "../../../../store";
import { ResultItem, updateResponse } from "../../../../store/features/responseSlice";

interface ViewerMainProps {
  mainRef: DivRef,
  loadingRef: DivRef,
  errorRef: DivRef,
  contentRef: DivRef
  fuaRef: InputRef,
  logPreRef: PreArrRef
  errorMessage: string,
}

export default function ViewerMain({ mainRef, fuaRef, loadingRef, errorRef, errorMessage, contentRef, logPreRef }: ViewerMainProps) {
  const iptRef: InputRef = useRef(null);
  const currentImgRef: DivRef = useRef(null);
  const cvsRef: CanvasArrRef = useRef([]);
  const [regionStyle, setRegionStyle] = useState({ width: "100%", height: "100%", top: "0", left: "0" });

  const globalSettings = useSelector((store: RootStore) => store.globalSettings);
  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const response = useSelector((store: RootStore) => store.decodeResponse);
  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const hoverIndex = useSelector((store: RootStore) => store.hoverIndex);
  const region = useSelector((store: RootStore) => store.region);
  const dispatch = useDispatch();

  const drawDecodeRegion = () => {
    if (currentImgRef.current) {
      const imgEle = currentImgRef.current.children[0] as HTMLImageElement;
      const regionDiv = currentImgRef.current.children[1] as HTMLDivElement;
      regionDiv.style.width = imgEle.clientWidth + "px";
      regionDiv.style.height = imgEle.clientHeight + "px";
    }
  }

  useEffect(() => {
    drawDecodeRegion();
    dispatch(updateResponse({ response: {} }));
  }, [generalStates.curImgIdx, dispatch]);

  useEffect(() => {
    if (JSON.stringify(response) === "{}") return;
    drawDecodeRegion();
    let mouseoverEvent: (e: MouseEvent) => void;
    let mouseoutEvent: (e: MouseEvent) => void;
    let resizeDrawResultCvs: () => void;
    let cvs: HTMLCanvasElement;
    let cr = contentRef.current!;
    if (currentImgRef.current) {
      for (let i = 0; i < response.results!.length; i++) {
        let resultForDraw = deepClone(response.results![i]) as ResultItem;
        cvs = drawResultCvs(resultForDraw, i);
        mouseoverEvent = (e) => {
          e.stopPropagation();
          dispatch(updateHoverIndex({ value: i, isChangeCvsStyle: false, isChangeLogStyle: true }));
        }
        mouseoutEvent = (e) => {
          e.stopPropagation();
          dispatch(updateHoverIndex({ value: -1, isChangeCvsStyle: false, isChangeLogStyle: true }));
        }
        cvs.addEventListener("mouseover", mouseoverEvent);
        cvs.addEventListener("mouseout", mouseoutEvent);
      }
      resizeDrawResultCvs = () => {
        for (let i = 0; i < response.results!.length; i++) {
          let resultForDraw = deepClone(response.results![i]) as ResultItem;
          drawResultCvs(resultForDraw, i);
        }
      }
      window.addEventListener("resize", drawDecodeRegion);
      window.addEventListener("resize", resizeDrawResultCvs);
      cr.addEventListener("transitionend", drawDecodeRegion);
      cr.addEventListener("transitionend", resizeDrawResultCvs);
    }

    return () => {
      for (let cvs of cvsRef.current) {
        cvs?.removeEventListener("mouseover", mouseoverEvent);
        cvs?.removeEventListener("mouseout", mouseoutEvent);
      }
      window.removeEventListener("resize", drawDecodeRegion);
      window.removeEventListener("resize", resizeDrawResultCvs);
      cr.removeEventListener("transitionend", drawDecodeRegion);
      cr.removeEventListener("transitionend", resizeDrawResultCvs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (!hoverIndex.isChangeCvsStyle) return;
    cvsRef.current = cvsRef.current.filter(Boolean);
    cvsRef.current.forEach((_, index) => {
      if (hoverIndex.index === index) {
        cvsRef.current[index]!.style.backgroundColor = "rgba(250,15,15,0.3)";
        cvsRef.current[index]!.style.boxShadow = "rgba(250,15,15,0.3) 0px 0px 0px 5px";
      } else {
        cvsRef.current[index]!.style.backgroundColor = "";
        cvsRef.current[index]!.style.boxShadow = "";
      }
    })
  }, [hoverIndex]);

  useEffect(() => {
    if (generalStates.curImgIdx === -1) return;
    if (region.top === undefined || region.left === undefined || region.right === undefined || region.bottom === undefined) return;
    if (!/px|%$/.test(region.top) || !/px|%$/.test(region.left) || !/px|%$/.test(region.right) || !/px|%$/.test(region.bottom)) return;
    const top = parseInt(region.top);
    const left = parseInt(region.left);
    const right = parseInt(region.right);
    const bottom = parseInt(region.bottom);
    let style;
    let pTop = top;
    let pBottom = bottom;
    let pLeft = left;
    let pRight = right;
    let imgOriWidth;
    let imgOriHeight;
    if (!region.isPercentage) {
      const copyRegion = deepClone(region);
      const imgEle = currentImgRef.current?.children[0] as HTMLImageElement;
      imgOriWidth = imgEle.naturalWidth;
      imgOriHeight = imgEle.naturalHeight;
      copyRegion.left = left > imgOriWidth ? imgOriWidth : left;
      copyRegion.right = right > imgOriWidth ? imgOriWidth : right;
      copyRegion.top = top > imgOriHeight ? imgOriHeight : top;
      copyRegion.bottom = bottom > imgOriHeight ? imgOriHeight : bottom;
      pTop = copyRegion.top / imgOriHeight * 100;
      pBottom = copyRegion.bottom / imgOriHeight * 100;
      pLeft = copyRegion.left / imgOriWidth * 100;
      pRight = copyRegion.right / imgOriWidth * 100;
    }
    style = { width: pRight - pLeft + "%", height: pBottom - pTop + "%", top: pTop + "%", left: pLeft + "%" };
    setRegionStyle(style);
  }, [region, generalStates.curImgIdx, imagesData])

  const drawResultCvs = (result: ResultItem, index: number): HTMLCanvasElement => {
    let cvs = cvsRef.current[index] as HTMLCanvasElement;
    let isPatchCode = result.formatString === "PatchCode";
    let oriImg = currentImgRef.current!.children[0] as HTMLImageElement;
    let pdfNaturaWH: string | Array<string>;
    let oriWidth: number = oriImg.naturalWidth;
    let oriHeight: number = oriImg.naturalHeight;
    let widthZoom: number;
    let heightZoom: number;

    if (imagesData[generalStates.curImgIdx].isPdf) {
      let dpiValue = globalSettings.PDFRasterDPI.value;
      if (globalSettings.PDFReadingMode.value[0] === 1) {
        dpiValue = 300;
      }
      let dpiRate = 72 / dpiValue;
      let rps = result.location;
      let p = rps[0].split(',') as any as number[];
      pdfNaturaWH = Math.floor(p[0] / ((p[0] * dpiRate / oriImg.naturalWidth * 100) / 100)) + ',' + Math.floor(p[1] / ((p[1] * dpiRate / oriImg.naturalHeight * 100 / 100)));
      pdfNaturaWH = pdfNaturaWH.split(",");
      oriWidth = parseInt(pdfNaturaWH[0]);
      oriHeight = parseInt(pdfNaturaWH[1]);
    }

    let oriWHRatio = oriWidth / oriHeight;
    let imgWidth = parseInt(getComputedStyle(oriImg).width);
    let imgHeight = parseInt(getComputedStyle(oriImg).height);
    let imgWHRatio = imgWidth / imgHeight;

    let ctx = cvs.getContext("2d") as CanvasRenderingContext2D;
    ctx.globalCompositeOperation = "destination-over";

    let x0, x1, x2, x3, y0, y1, y2, y3;
    let rps = result.location;

    let p0 = rps[0].split(',');
    x0 = parseInt(p0[0]);
    y0 = parseInt(p0[1]);
    let p1 = rps[1].split(',');
    x1 = parseInt(p1[0]);
    y1 = parseInt(p1[1]);
    let p2 = rps[2].split(',');
    x2 = parseInt(p2[0]);
    y2 = parseInt(p2[1]);
    let p3 = rps[3].split(',');
    x3 = parseInt(p3[0]);
    y3 = parseInt(p3[1]);

    let maxX = Math.max(x0, x1, x2, x3);
    let minX = Math.min(x0, x1, x2, x3);
    let maxY = Math.max(y0, y1, y2, y3);
    let minY = Math.min(y0, y1, y2, y3);

    if (oriWHRatio > imgWHRatio) {
      widthZoom = imgWidth / oriWidth;
      heightZoom = imgWidth / oriWHRatio / oriHeight;
      cvs.style.top = minY * heightZoom + (imgHeight - imgWidth / oriWHRatio) * 0.5 + "px";
      cvs.style.left = minX * widthZoom + "px";
      cvs.width = (maxX - minX) * widthZoom;
      cvs.height = (maxY - minY) * widthZoom;
      cvs.style.width = (maxX - minX) * widthZoom + "px";
      cvs.style.height = (maxY - minY) * widthZoom + "px";
    } else {
      heightZoom = imgHeight / oriHeight;
      widthZoom = (imgHeight * oriWHRatio) / oriWidth;
      cvs.style.top = minY * heightZoom + "px";
      cvs.style.left = minX * widthZoom + (imgWidth - imgHeight * oriWHRatio) * 0.5 + "px";
      cvs.width = (maxX - minX) * heightZoom;
      cvs.height = (maxY - minY) * heightZoom;
      cvs.style.width = (maxX - minX) * heightZoom + "px";
      cvs.style.height = (maxY - minY) * heightZoom + "px";
    }
    cvs.style.position = "absolute";
    x0 = x0 - minX; y0 = y0 - minY;
    x1 = x1 - minX; y1 = y1 - minY;
    x2 = x2 - minX; y2 = y2 - minY;
    x3 = x3 - minX; y3 = y3 - minY;

    cvs.width = cvs.width < Math.max(x0, x1, x2, x3) ? Math.max(x0, x1, x2, x3) : cvs.width;
    cvs.height = cvs.height < Math.max(y0, y1, y2, y3) ? Math.max(y0, y1, y2, y3) : cvs.height;

    if (isPatchCode) {
      ctx.fillStyle = "rgba(97, 189, 79, 0.2)";
      cvs.style.zIndex = "1";
    } else {
      ctx.fillStyle = "rgba(97, 189, 79, 0.5)";
      cvs.style.zIndex = "2";
    }

    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.lineTo(x0, y0);
    ctx.fill();

    return cvs;
  };

  const showFilePicker = (): void => {
    iptRef.current!.click();
  }

  const handleIptChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    handleFiles(e.target.files as unknown as File[], loadingRef, dispatch);
    iptRef.current!.value = "";
  }

  const handleDropEvent = async (e: DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer!.files as unknown as File[], loadingRef, dispatch);
    fuaRef.current!.style.borderColor = "#ddd";
  }

  const handleDragOverEvent = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    fuaRef.current!.style.borderColor = "rgb(80,168,225)";
  }

  const handleDragLeaveEvent = (): void => {
    fuaRef.current!.style.borderColor = "#ddd";
  }

  const showCurrentImage = () => {
    return imagesData.map((item: DemoImageData, index: number) => {
      return (
        <div className="dbr-image-box" key={index} style={{ display: generalStates.curImgIdx === index ? "flex" : "none" }} ref={generalStates.curImgIdx === index ? currentImgRef : null}>
          <img src={item.url ? item.url : require(`../../../../assets/image/${item.name}`)} alt="img" />
          <div className="dbr-decode-region">
            <div className="dbr-region-border" style={regionStyle}></div>
            {resultCvs(index)}
          </div>
        </div>
      )
    });
  }

  const resultCvs = (index: number) => {
    if (index === generalStates.curImgIdx) {
      if (response.results && response.results.length) {
        return response.results.map((item, index) => {
          const isLink = checkIsLink(item.text);
          return (
            <span key={index}>
              <canvas
                id={`dbr-decode-result-${index}`}
                className="dbr-decode-result"
                ref={(node: HTMLCanvasElement) => { cvsRef.current[index] = node }}
                onClick={() => { openResult(index) }}>
              </canvas>
              <Tooltip
                anchorSelect={`#dbr-decode-result-${index}`}
                className="dbr-decode-result-tooltip"
                clickable
              >
                {
                  isLink ? <a href={`https://${item.text.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer">{item.text}</a> : item.text
                }
              </Tooltip>
            </span>
          )
        })
      }
    } else {
      return null;
    }
  }

  const openResult = (index: number) => {
    animation(logPreRef.current[index], 2);
  }

  return (
    <div className="dbr-viewer-main" ref={mainRef} onDrop={handleDropEvent} onDragOver={handleDragOverEvent} onDragLeave={handleDragLeaveEvent}>
      <Loading errorMessage={errorMessage} loadingRef={loadingRef} errorRef={errorRef} />
      <div className="dbr-file-upload-area" ref={fuaRef} onClick={showFilePicker}>
        <input type="file" accept="image/bmp,image/gif,image/jpeg,image/png,image/webp,application/pdf,image/tiff" multiple style={{ display: "none" }} ref={iptRef} onChange={handleIptChange} />
        <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 67 67" xmlSpace="preserve"><g><path className="st0" d="M54.9,52.9V2c0-1.1-0.9-2-2-2H2C0.9,0,0,0.9,0,2v50.9c0,1.1,0.9,2,2,2h50.9C54,54.9,54.9,54,54.9,52.9z M2,2h50.9v41.5h-7.7l-7.9-19.8c-0.6-1.6-1.6-1.8-2.1-1.8c-0.5,0-1.5,0.2-2.2,1.7l-5.8,12.8l-4.7-5.6c-1-1.4-2.9-1.8-4.3-0.8c-0.5,0.3-0.8,0.7-1,1.1L9.8,43.5H2L2,2L2,2z M43.1,43.5h-31L19,32.1c0.1-0.2,0.2-0.3,0.4-0.4c0.5-0.3,1.2-0.2,1.6,0.4l5.8,6.9c0.2,0.3,0.6,0.4,0.9,0.4c0.3,0,0.6-0.3,0.8-0.6l6.5-14.3c0.1-0.3,0.2-0.4,0.3-0.5c0.1,0.1,0.2,0.2,0.3,0.5L43.1,43.5z M2,45.5h50.9v7.3H2L2,45.5z" /><path className="st0" d="M63.3,18.6l-3.1-0.3c-0.6,0-1,0.4-1.1,0.9c0,0.6,0.4,1,0.9,1.1l3.1,0.3c1.2,0.1,2,1.1,1.9,2.3l-3.7,40.3c-0.1,1.2-1.1,2-2.3,1.9l-43.3-3.9c-0.5,0-1,0.4-1.1,0.9c0,0.5,0.4,1,0.9,1.1L58.9,67c0.1,0,0.3,0,0.4,0c2.1,0,3.9-1.6,4.1-3.7L67,23C67.2,20.8,65.6,18.8,63.3,18.6z" /><path className="st0" d="M15,20.7c3.1,0,5.7-2.5,5.7-5.7S18.1,9.3,15,9.3S9.3,11.9,9.3,15S11.9,20.7,15,20.7z M15,11.3c2,0,3.7,1.6,3.7,3.7c0,2-1.6,3.7-3.7,3.7c-2,0-3.7-1.6-3.7-3.7C11.3,13,13,11.3,15,11.3z" /></g></svg>
        <div className="dbr-drag-upload-tip">
          <div className="dbr-drag-title">Drag images or PDF files here</div>
          <div className="dbr-sub-disc">Support png, jpg, bmp, gif, webp, tif, pdf</div>
        </div>
        <div className="dbr-bottom-text">
          <div className="dbr-mobile-text">
            Upload images (.png, .jpg, .bmp, <br />
            .webp, .gif, .tif) or PDF files here
          </div>
          <div>Or click the sample images</div>
          <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4898" width="30" height="30"><path d="M834.56 680.96c-8.533333-8.533333-22.186667-6.826667-29.013333 0L532.48 954.026667l0-931.84C532.48 5.12 520.533333 0 512 0c-8.533333 0-20.48 8.533333-20.48 20.48l0 931.84L218.453333 680.96c-8.533333-8.533333-20.48-8.533333-29.013333 0-8.533333 8.533333-6.826667 22.186667 0 29.013333l305.493333 305.493333c0 0 1.706667 1.706667 1.706667 1.706667l0 0c0 0 0 0 0 0 3.413333 3.413333 8.533333 5.12 13.653333 5.12l0 0c3.413333 0 6.826667-1.706667 8.533333-1.706667 1.706667 0 3.413333-1.706667 5.12-3.413333l307.2-307.2C841.386667 703.146667 843.093333 691.2 834.56 680.96z" fill="#272636" p-id="4899"></path></svg>
        </div>
      </div>
      <div className="dbr-main-image">
        {showCurrentImage()}
      </div>
    </div>
  )
}
